import { Badge, Box, Table, Text, Tooltip, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { AiEffectiveConfig } from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

interface EffectiveConfigCardProps {
  overview: AiEffectiveConfig | null;
  loading: boolean;
}

/** Colour by how specific the winning rule was — a platform default is the weakest answer. */
const sourcePalette: Record<string, string> = {
  "route-binding": "blue",
  "agent-config": "purple",
  "call-override": "teal",
  "platform-default": "gray",
};

/**
 * What every route actually resolves to right now, and which rule decided it.
 *
 * This is the answer to "I changed a setting, why is nothing different" — the resolution chain has
 * several steps and a more specific one further up silently wins. Showing the deciding rule turns
 * that from a support ticket into a glance.
 */
const EffectiveConfigCard = ({
  overview,
  loading,
}: EffectiveConfigCardProps) => {
  const { t } = useTranslation();

  if (!overview) {
    return (
      <Text color={getThemeColor("text.secondary")}>
        {loading ? t("app.common.loading") : t("app.aiGateway.effective.empty")}
      </Text>
    );
  }

  return (
    <VStack align="stretch" gap={4}>
      <Text fontSize="sm" color={getThemeColor("text.secondary")}>
        {t("app.aiGateway.effective.subtitle")}
      </Text>

      <Box overflowX="auto">
        <Table.Root size="sm" variant="outline" minW="820px">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>
                {t("app.aiGateway.table.route")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.aiGateway.table.plane")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.aiGateway.table.model")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.aiGateway.table.connector")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.aiGateway.table.resolvedBy")}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {overview.routes.map((route) => (
              <Table.Row key={route.agentRoute ?? route.model}>
                <Table.Cell>
                  <Text fontWeight="medium">
                    {route.agentRoute ??
                      t("app.aiGateway.effective.unnamedRoute")}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    colorPalette={route.plane === "Platform" ? "gray" : "green"}
                    size="sm"
                  >
                    {t(`app.aiGateway.planes.${route.plane}`)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <VStack align="start" gap={0}>
                    <Text>{route.model}</Text>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {route.provider}
                    </Text>
                  </VStack>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">
                    {route.connectorName ??
                      t("app.aiGateway.effective.noConnector")}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Tooltip content={route.explanation}>
                    <Badge
                      colorPalette={sourcePalette[route.resolvedBy] ?? "gray"}
                      size="sm"
                    >
                      {t(`app.aiGateway.resolvedBy.${route.resolvedBy}`, {
                        defaultValue: route.resolvedBy,
                      })}
                    </Badge>
                  </Tooltip>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </VStack>
  );
};

export default EffectiveConfigCard;
